import { z } from 'zod';
/**
 * Boolean from an env string. NOTE: `z.coerce.boolean()` treats "false" as true (Boolean("false")),
 * so never use it for flags — use this.
 */
export const boolEnv = (defaultValue = false) => z.preprocess((v) => {
    if (v === undefined || v === '')
        return defaultValue;
    if (typeof v === 'boolean')
        return v;
    return ['1', 'true', 'yes', 'on'].includes(String(v).trim().toLowerCase());
}, z.boolean());
/**
 * Standard environment variable names shared by every app (docs/CONVENTIONS.md §B.10, §H.57).
 * Compose the pieces you need: `loadEnv(baseEnv.merge(osEnv).merge(databaseEnv).extend({...}))`.
 */
export const baseEnv = z.object({
    NODE_ENV: z.enum(['development', 'test', 'staging', 'production']).default('development'),
    PORT: z.coerce.number().int().positive(),
    /**
     * Interface the API binds. Default `0.0.0.0` (IPv4, every interface), the same rule as Vite (docs/PORTS.md): Node's own
     * default is the IPv6 wildcard `::`, which the Caddy dev proxy cannot reach from Docker on WSL2 (WSL forwards an IPv6
     * wildcard bind only to `::1` on Windows; Docker's `host.docker.internal` relay connects over IPv4 → 502).
     */
    HOST: z.string().min(1).default('0.0.0.0'),
    LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace']).default('info'),
});
/** How an API trusts the OS issuer. */
export const osEnv = z.object({
    OS_ISSUER: z.string().url(),
    OS_JWKS_URL: z.string().url(),
    /** This app's id == expected `aud`. */
    OS_CLIENT_ID: z.string().min(1),
    /** Development only: skip token verification and act as a seeded identity. Refused in production. */
    AUTH_DEV_BYPASS: boolEnv(false),
});
export const databaseEnv = z.object({
    DATABASE_URL: z.string().min(1),
    SHADOW_DATABASE_URL: z.string().optional(),
});
export const redisEnv = z.object({ REDIS_URL: z.string().min(1) });
export const s3Env = z.object({
    S3_ENDPOINT: z.string().url(),
    S3_REGION: z.string().default('ap-southeast-2'),
    S3_BUCKET: z.string().min(1),
    S3_ACCESS_KEY: z.string().min(1),
    S3_SECRET_KEY: z.string().min(1),
    S3_FORCE_PATH_STYLE: boolEnv(true),
});
export const smtpEnv = z.object({
    SMTP_HOST: z.string().default('localhost'),
    SMTP_PORT: z.coerce.number().default(1026),
    SMTP_FROM: z.string().default('no-reply@os.localhost'),
});
/** Web (Vite) side: `VITE_OS_ISSUER`, `VITE_OS_CLIENT_ID`, `VITE_AUTH_DEV`. */
export const viteOsEnv = z.object({
    VITE_OS_ISSUER: z.string().url(),
    VITE_OS_CLIENT_ID: z.string().min(1),
    VITE_AUTH_DEV: z.enum(['true', 'false']).default('false'),
});
/** Parse once at boot; fail fast with a readable list; forbid dev bypasses in production. */
export function loadEnv(schema, opts = {}) {
    const source = opts.source ?? globalThis.process?.env ?? {};
    const parsed = schema.safeParse(source);
    if (!parsed.success) {
        const detail = parsed.error.issues.map((i) => `  ${i.path.join('.')}: ${i.message}`).join('\n');
        throw new Error(`Invalid environment configuration:\n${detail}`);
    }
    const data = parsed.data;
    if (data.NODE_ENV === 'production' && data.AUTH_DEV_BYPASS === true) {
        throw new Error('AUTH_DEV_BYPASS must be false in production');
    }
    return parsed.data;
}
//# sourceMappingURL=env.js.map